import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import CheckIcon from '@mui/icons-material/Check';
import { useSelector, useDispatch } from 'react-redux';
import { createBatch, updateBatch, updateBatchReason } from 'thunks/trainingThunks/thunk';
import 'react-toastify/dist/ReactToastify.css';
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import { restForm } from 'reducer/trainingSlice';
import { Box, TextField, Typography } from '@mui/material';
import useIsLearningSuperUser from 'hooks/useIsLearningSuperUser';

export default function AlertDialog({ disabled }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const [updateReason, setUpdateReason] = React.useState('');
  const [selectedFile, setSelectedFile] = React.useState(null);
  const [fileError, setFileError] = React.useState('');
  const dispatch = useDispatch();
  const stateValues = useSelector((state) => state.trainingSlice);
  const isLearningSuperUser = useIsLearningSuperUser();

  // Check if we're in edit mode by looking for batch parameter in URL
  const queryParams = new URLSearchParams(location.search);
  const batchId = queryParams.get('batch');
  const isEditMode = !!batchId;

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setUpdateReason('');
    setSelectedFile(null);
    setFileError('');
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check file type
      const validTypes = [
        'image/jpeg',
        'image/png',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];
      if (!validTypes.includes(file.type)) {
        setFileError('Please upload only images (JPEG, PNG), PDF, or Word documents');
        setSelectedFile(null);
        return;
      }

      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setFileError('File size should be less than 5MB');
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setFileError('');
    }
  };

  const proceed = async () => {
    if (isEditMode && isLearningSuperUser) {
      if (!updateReason.trim()) {
        toast.error('Please provide a reason for the update', {
          theme: 'colored',
        });
        return;
      }
    }

    setOpen(false);
    try {
      if (isEditMode) {
        // First update the batch
        const updateResponse = await dispatch(updateBatch({ updateReason, selectedFile }));

        // If super user, also send the reason and attachment
        console.log('isLearningSuperUser stateValues', isLearningSuperUser, stateValues);

        if (isLearningSuperUser) {
          // Get the updatedLogDocumentId from the response
          const updatedLogDocumentId = updateResponse?.data?.data?._id;
          console.log('updatedLogDocumentId from response', updatedLogDocumentId);

          if (updatedLogDocumentId) {
            const formData = new FormData();
            formData.append('batchId', batchId);
            formData.append('updateReason', updateReason);
            formData.append('logDocumentId', updatedLogDocumentId);
            if (selectedFile) {
              formData.append('supportingDocument', selectedFile);
            }
            await dispatch(updateBatchReason(formData));
          } else {
            console.error('No updatedLogDocumentId received from batch update');
          }
        }

        toast.success('Batch Updated Successfully', {
          theme: 'colored',
        });
      } else {
        await dispatch(createBatch());
        toast.success('Batch Created Successfully', {
          theme: 'colored',
        });
      }
      await dispatch(restForm());
      navigate('/training/AllTrainings');
    } catch (error) {
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} batch: ${error.message}`, {
        theme: 'colored',
      });
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleClickOpen}
          startIcon={<CheckIcon />}
          size="small"
          sx={{
            minWidth: '120px',
            height: '36px',
            fontSize: '0.875rem',
          }}
          disabled={disabled}
        >
          {isEditMode ? 'Update Batch' : 'Create Batch'}
        </Button>
      </Box>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="alert-dialog-title">
          {isEditMode ? 'Update Batch' : 'Create Batch'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description" sx={{ mb: 2 }}>
            Are you sure you want to {isEditMode ? 'update' : 'create'} this batch?
          </DialogContentText>

          {isEditMode && isLearningSuperUser && (
            <>
              <TextField
                fullWidth
                label="Reason for Update"
                multiline
                rows={3}
                value={updateReason}
                onChange={(e) => setUpdateReason(e.target.value)}
                required
                sx={{ mb: 2 }}
              />

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Supporting Document (Optional - Images, PDF, or Word documents only, max 5MB)
                </Typography>
                <input
                  accept=".jpg,.jpeg,.png,.pdf,.doc,.docx"
                  style={{ display: 'none' }}
                  id="raised-button-file"
                  type="file"
                  onChange={handleFileChange}
                />
                <label htmlFor="raised-button-file">
                  <Button variant="outlined" component="span">
                    Upload File
                  </Button>
                </label>
                {selectedFile && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Selected file: {selectedFile.name}
                  </Typography>
                )}
                {fileError && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    {fileError}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={proceed} autoFocus>
            {isEditMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
